import React, { useState, useEffect, useCallback, useRef } from 'react';

import './TutorialCompany.scss';

// Chave usada apenas como cache local (evita "piscar" o tutorial
// enquanto a flag do backend ainda está carregando).
const STORAGE_KEY = 'tutorialCompany';

const BUBBLE_WIDTH = 400;
const BUBBLE_HEIGHT_ESTIMATE = 200;
const BUBBLE_MARGIN = 16;
const REPOSITION_DELAY_MS = 300;

// Configuração dos passos
const STEPS = [
    {
        id: 'welcome',
        title: 'Bem-vindo à FreelancersApp!',
        description:
            'Este tutorial rápido mostra os principais pontos do seu painel corporativo.',
        selector: null,
        align: 'center'
    },
    {
        id: 'profile',
        title: '👤 Seu Perfil',
        description:
            'Aqui você vê seu nome e o plano GOLD ativo. Clique para editar suas informações.',
        selector: '.dashboard-company__user-info',
        align: 'bottom'
    },
    {
        id: 'gold-badge',
        title: '🏅 Plano GOLD',
        description:
            'Você está no plano GOLD! Isso dá acesso a recursos exclusivos como prioridade em candidaturas.',
        selector: '.dashboard-company__plan-badge',
        align: 'bottom'
    },
    {
        id: 'wallet',
        title: '💰 Carteira',
        description:
            'Seu saldo disponível para contratar freelancers. Clique para ver detalhes.',
        selector: '.dashboard-sidebar__info-item--wallet',
        align: 'bottom'
    },
    {
        id: 'premium',
        title: '⭐ Premium',
        description:
            'Acesse planos e benefícios exclusivos para sua empresa.',
        selector: '.dashboard-company__action-btn--premium',
        align: 'bottom'
    },
    {
        id: 'favorites',
        title: '❤️ Favoritos',
        description:
            'Veja e gerencie seus freelancers favoritos.',
        selector: '.dashboard-company__action-btn--favorites',
        align: 'bottom'
    },
    {
        id: 'settings',
        title: '⚙️ Configurações',
        description:
            'Altere suas preferências e dados da conta.',
        selector: '.dashboard-company__action-btn--profile',
        align: 'bottom'
    },
    {
        id: 'new-job',
        title: '➕ Nova Vaga',
        description:
            'Crie uma nova vaga para encontrar o freelancer ideal.',
        selector: '.dashboard-company__action-btn--new-job',
        align: 'bottom'
    },
    {
        id: 'tabs',
        title: '📋 Navegação',
        description:
            'Navegue entre Agenda, Vagas Abertas, Em Andamento e Finalizadas.',
        selector: '.dashboard-company__nav-tabs',
        align: 'bottom'
    },
    {
        id: 'agenda',
        title: '📅 Agenda',
        description:
            'Aqui você vê todas as suas vagas organizadas por dia da semana.',
        selector: '.company-agenda',
        align: 'bottom'
    }
];

const getCenterPosition = () => ({
    top: window.innerHeight / 2 - BUBBLE_HEIGHT_ESTIMATE / 2,
    left: window.innerWidth / 2 - BUBBLE_WIDTH / 2
});

const getPositionForElement = (element) => {
    const rect = element.getBoundingClientRect();

    let top = rect.bottom + BUBBLE_MARGIN;
    let left = rect.left + rect.width / 2 - BUBBLE_WIDTH / 2;

    // Não deixar sair da esquerda
    if (left < BUBBLE_MARGIN) {
        left = BUBBLE_MARGIN;
    }

    // Não deixar sair da direita
    if (left + BUBBLE_WIDTH > window.innerWidth) {
        left = window.innerWidth - BUBBLE_WIDTH - BUBBLE_MARGIN;
    }

    // Se não couber embaixo, coloca em cima
    if (top + BUBBLE_HEIGHT_ESTIMATE > window.innerHeight) {
        top = rect.top - BUBBLE_HEIGHT_ESTIMATE - BUBBLE_MARGIN;
    }

    // Evita ficar acima da tela
    if (top < BUBBLE_MARGIN) {
        top = BUBBLE_MARGIN;
    }

    return { top, left };
};

/**
 * Tutorial guiado do painel da empresa.
 *
 * A visibilidade é controlada pelo backend, não só pelo navegador:
 * - `hasSeenTutorial` deve refletir uma flag ligada à conta da empresa
 *   (ex.: `company.hasSeenTutorial`), setada quando a empresa marca
 *   "Não mostrar novamente".
 * - `onComplete(dontShowAgain)` é chamado ao pular ou concluir o tutorial,
 *   com `dontShowAgain` = true se o checkbox estiver marcado. É nesse
 *   callback que a tela pai deve persistir a flag no backend — ex.:
 *   `api.patch('/companies/{id}/tutorial-seen')` — para o tutorial parar
 *   de aparecer em qualquer dispositivo/navegador.
 *
 * Sem marcar o checkbox, fechar o tutorial (Pular ou Concluir) só o
 * esconde na sessão atual; ele volta a aparecer no próximo login,
 * já que nada foi persistido.
 *
 * O localStorage é usado só como cache otimista do "não mostrar
 * novamente", pra não piscar o tutorial na tela enquanto a flag do
 * backend ainda está carregando.
 */
const TutorialCompany = ({ hasSeenTutorial = false, onComplete, onFinish }) => {
    const [step, setStep] = useState(0);
    const [position, setPosition] = useState(getCenterPosition);
    const [dontShowAgain, setDontShowAgain] = useState(false);
    const [isVisible, setIsVisible] = useState(
        () => !hasSeenTutorial && localStorage.getItem(STORAGE_KEY) !== 'true'
    );
    const repositionTimeoutRef = useRef(null);

    // Se o backend informar (ou atualizar) que a empresa já viu o
    // tutorial, escondemos imediatamente — cobre o caso de o valor
    // chegar depois da primeira renderização (ex.: via fetch async).
    useEffect(() => {
        if (hasSeenTutorial) {
            setIsVisible(false);
        }
    }, [hasSeenTutorial]);

    const clearHighlights = useCallback(() => {
        document
            .querySelectorAll('.tutorial-highlight')
            .forEach((el) => el.classList.remove('tutorial-highlight'));
    }, []);

    const finishTutorial = useCallback(() => {
        setIsVisible(false);
        clearHighlights();

        if (dontShowAgain) {
            // Cache local otimista — evita reaparecer nesta mesma aba
            // enquanto a chamada ao backend ainda está em andamento.
            localStorage.setItem(STORAGE_KEY, 'true');

            if (onComplete) {
                onComplete(true);
            }
        }

        if (onFinish) {
            onFinish(dontShowAgain);
        }
    }, [clearHighlights, dontShowAgain, onComplete, onFinish]);

    const handleNext = useCallback(() => {
        setStep((prev) => {
            if (prev < STEPS.length - 1) {
                return prev + 1;
            }

            finishTutorial();
            return prev;
        });
    }, [finishTutorial]);

    const handleSkip = useCallback(() => {
        finishTutorial();
    }, [finishTutorial]);

    // Atalhos de teclado: Esc pula, Enter/seta direita avança
    useEffect(() => {
        if (!isVisible) return undefined;

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                handleSkip();
            } else if (event.key === 'Enter' || event.key === 'ArrowRight') {
                handleNext();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isVisible, handleNext, handleSkip]);

    // Posiciona o balão do passo atual e mantém a posição correta em
    // resize/scroll da janela.
    useEffect(() => {
        if (!isVisible) return undefined;

        const currentStep = STEPS[step];
        if (!currentStep) return undefined;

        clearHighlights();

        const reposition = () => {
            if (!currentStep.selector) {
                setPosition(getCenterPosition());
                return;
            }

            const element = document.querySelector(currentStep.selector);

            if (!element) {
                setPosition(getCenterPosition());
                return;
            }

            element.classList.add('tutorial-highlight');
            setPosition(getPositionForElement(element));
        };

        const element = currentStep.selector
            ? document.querySelector(currentStep.selector)
            : null;

        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        // Pequeno atraso para pegar a posição depois do scroll
        repositionTimeoutRef.current = setTimeout(reposition, REPOSITION_DELAY_MS);

        window.addEventListener('resize', reposition);
        window.addEventListener('scroll', reposition, true);

        return () => {
            clearTimeout(repositionTimeoutRef.current);
            window.removeEventListener('resize', reposition);
            window.removeEventListener('scroll', reposition, true);
            clearHighlights();
        };
    }, [step, isVisible, clearHighlights]);

    if (!isVisible) {
        return null;
    }

    const currentStep = STEPS[step];
    const isLast = step === STEPS.length - 1;

    return (
        <div className="tutorial-overlay">
            <div
                className="tutorial-bubble"
                role="dialog"
                aria-modal="true"
                aria-labelledby="tutorial-title"
                aria-describedby="tutorial-description"
                style={{
                    top: position.top,
                    left: position.left,
                    position: 'fixed'
                }}
            >
                <div className="tutorial-step-indicator">
                    {step + 1} / {STEPS.length}
                </div>

                <h3 id="tutorial-title" className="tutorial-title">
                    {currentStep.title}
                </h3>

                <p id="tutorial-description" className="tutorial-description">
                    {currentStep.description}
                </p>

                <label className="tutorial-dont-show-again">
                    <input
                        type="checkbox"
                        checked={dontShowAgain}
                        onChange={(event) => setDontShowAgain(event.target.checked)}
                    />
                    Não mostrar novamente
                </label>

                <div className="tutorial-actions">
                    <button
                        type="button"
                        className="tutorial-skip"
                        onClick={handleSkip}
                    >
                        Pular
                    </button>

                    <button
                        type="button"
                        className="tutorial-next"
                        onClick={handleNext}
                    >
                        {isLast ? '✅ Concluir' : 'Próximo →'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TutorialCompany;